import { useState, type ButtonHTMLAttributes, type CSSProperties, type ReactNode } from 'react'

export type AdminPanelButtonVariant = 'default' | 'primary' | 'danger' | 'active'

type AdminPanelButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  children: ReactNode
  variant?: AdminPanelButtonVariant
  size?: 'sm' | 'md' | 'icon'
}

function baseStyles(
  variant: AdminPanelButtonVariant,
  hover: boolean,
  disabled: boolean | undefined,
  size: 'sm' | 'md' | 'icon'
): CSSProperties {
  const pad =
    size === 'icon' ? '8px' : size === 'sm' ? '6px 12px' : '8px 16px'
  const fontSize = size === 'sm' ? 12 : 13

  if (variant === 'primary') {
    return {
      padding: pad,
      borderRadius: 8,
      border: `1px solid ${hover && !disabled ? 'var(--gold)' : 'var(--gold-bd)'}`,
      background: hover && !disabled ? '#E8C96A' : 'var(--gold)',
      color: 'var(--navy)',
      fontSize,
      fontWeight: 500,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.55 : 1,
      transition:
        'border-color .15s ease, background .15s ease, color .15s ease, box-shadow .15s ease',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      boxShadow:
        hover && !disabled
          ? '0 2px 10px rgba(201,168,76,.35)'
          : '0 2px 8px rgba(201,168,76,.25)',
    }
  }

  if (variant === 'active') {
    return {
      padding: pad,
      borderRadius: 8,
      border: `1px solid ${hover && !disabled ? 'var(--navy)' : 'var(--navy)'}`,
      background: hover && !disabled ? '#1e3a5f' : 'var(--navy)',
      color: 'var(--white)',
      fontSize,
      fontWeight: 500,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.55 : 1,
      transition:
        'border-color .15s ease, background .15s ease, color .15s ease, box-shadow .15s ease',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      boxShadow: hover && !disabled ? '0 2px 8px rgba(15,23,42,.25)' : 'var(--sh-sm)',
    }
  }

  if (variant === 'danger') {
    return {
      padding: pad,
      borderRadius: 8,
      border: `1px solid ${hover && !disabled ? 'rgba(239,68,68,.55)' : 'rgba(239,68,68,.3)'}`,
      background: hover && !disabled ? 'rgba(239,68,68,.08)' : 'var(--white)',
      color: hover && !disabled ? '#DC2626' : '#F87171',
      fontSize,
      fontWeight: 500,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.55 : 1,
      transition:
        'border-color .15s ease, background .15s ease, color .15s ease, box-shadow .15s ease',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      boxShadow: hover && !disabled ? '0 2px 8px rgba(239,68,68,.15)' : 'var(--sh-sm)',
    }
  }

  return {
    padding: pad,
    borderRadius: 8,
    border: `1px solid ${hover && !disabled ? 'var(--gold-bd)' : 'var(--bd)'}`,
    background: hover && !disabled ? 'var(--gold-lt)' : 'var(--white)',
    color: disabled ? 'var(--muted)' : hover ? 'var(--navy)' : 'var(--text)',
    fontSize,
    fontWeight: 500,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.55 : 1,
    transition:
      'border-color .15s ease, background .15s ease, color .15s ease, box-shadow .15s ease',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    boxShadow: hover && !disabled ? '0 2px 8px rgba(201,168,76,.22)' : 'var(--sh-sm)',
  }
}

export function AdminPanelButton({
  children,
  variant = 'default',
  size = 'md',
  disabled,
  style,
  ...props
}: AdminPanelButtonProps) {
  const [hover, setHover] = useState(false)

  return (
    <button
      type="button"
      disabled={disabled}
      {...props}
      onMouseEnter={(e) => {
        setHover(true)
        props.onMouseEnter?.(e)
      }}
      onMouseLeave={(e) => {
        setHover(false)
        props.onMouseLeave?.(e)
      }}
      style={{
        ...baseStyles(variant, hover, disabled, size),
        ...style,
      }}
    >
      {children}
    </button>
  )
}
